import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faSun,
  faMoon,
  faCircleHalfStroke,
  faHome,
  faUserShield,
  faIdCard,
  faCreditCard,
  faClipboard,
  faUsers,
  faCog,
  faSignOutAlt,
  faChevronRight,
  faChevronUp,
  faTachometerAlt,
  faRocket,
  faEye,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import NotificationDropdown from "./notifications/NotificationDropdown";
import { useTheme } from "./context/ThemeContext";
import { useTranslation } from "../../i18n/LanguageContext";
import { useSubscription } from "./context/SubscriptionContext";
import { useEdition } from "./context/EditionContext";

function HeaderAppDesktop({
  isAuthenticated,
  userName,
  canSeeAccountMenu,
  canSeeGroupInfo,
  canSeeUsersMenu,
  canSeeSubscriptionMenu,
  showUserDropdown,
  dropdownRef,
  handleClick,
  toggleUserDropdown,
  setHeroContent,
  handleAccountClick,
  handleAccountProfileClick,
  handleChangePasswordClick,
  handleSettingsClick,
  handleUsersClick,
  handleSubscriptionsClick,
  handlePaymentsClick,
  handleSignOutClick,
  theme,
  setTheme,
  canAccessFunctionalFeatures,
  getAccessDenialReason,
  unreadCount,
  selectedUser,
  selectUser,
  loggedInUser,
  hasActiveSubscription,
  handleSwitchBack,
  isReadOnlyView,
  heroContent,
  isDeactivated,
}) {
  const { effectiveTheme } = useTheme();
  const { t, language, setLanguage } = useTranslation();
  const { canShare } = useSubscription();
  const { isCorporate } = useEdition();
  const isDarkTheme = effectiveTheme === "dark";

  const cycleLang = () => {
    setLanguage(language.startsWith("es") ? "en" : "es");
  };

  const cycleTheme = () => {
    const next =
      theme === "light" ? "dark" : theme === "dark" ? "auto" : "light";
    setTheme(next);
  };

  const themeIcon =
    theme === "dark" ? faMoon : theme === "auto" ? faCircleHalfStroke : faSun;
  const themeTitle =
    theme === "dark"
      ? t("theme.dark")
      : theme === "auto"
        ? t("theme.auto")
        : t("theme.light");
  const themeLabel =
    theme === "dark"
      ? t("header.theme.dark")
      : theme === "auto"
        ? t("header.theme.auto")
        : t("header.theme.light");
  const langLabel = language.startsWith("es")
    ? t("header.lang.spanish")
    : t("header.lang.english");

  // Check if the user has completed phone verification
  const isPhoneVerified = loggedInUser?.phoneVerified === true;

  // Mirror Hero.js green-border logic: only show "Viewing as" when a member's page is displayed
  const isRelevantScreen =
    heroContent === "Simulation Profile" ||
    heroContent === "Member User Info" ||
    heroContent === "Member User Info:Create" ||
    heroContent === "Member User Info:Update" ||
    heroContent === "Member User Info:Details" ||
    heroContent === "Member User Info:ResendVerification" ||
    heroContent === "Member Activity";
  const showViewingAs =
    isAuthenticated &&
    selectedUser &&
    loggedInUser &&
    selectedUser.uuid !== loggedInUser.uuid &&
    (isReadOnlyView || isRelevantScreen);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="desktop-nav">
        {!isReadOnlyView && (
          <a
            href="#backtomain"
            onClick={(e) => {
              console.log(
                "🖱️ HeaderAppDesktop: Back to Main Site clicked directly!",
              );
              handleClick(e);
            }}
          >
            <FontAwesomeIcon icon={faHome} /> {t("header.backToMain")}
          </a>
        )}
        {isAuthenticated && !isReadOnlyView && (
          <>
            {!isDeactivated && (
              <a
                href="#dashboard"
                onClick={handleClick}
                className={heroContent === "Dashboard" ? "nav-active" : ""}
              >
                <FontAwesomeIcon icon={faTachometerAlt} />{" "}
                {t("header.dashboard")}
              </a>
            )}
            {!isDeactivated &&
            (!localStorage.getItem("gettingStarted_graduated") ||
              localStorage.getItem("gettingStarted_graduated") !== "true") ? (
              <a
                href="#gettingstarted"
                onClick={handleClick}
                className={[
                  !canAccessFunctionalFeatures ? "disabled-link" : "",
                  heroContent === "Getting Started" ? "nav-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  opacity: !canAccessFunctionalFeatures ? 0.5 : 1,
                  cursor: !canAccessFunctionalFeatures
                    ? "not-allowed"
                    : "pointer",
                  pointerEvents: !canAccessFunctionalFeatures ? "none" : "auto",
                }}
                title={
                  !canAccessFunctionalFeatures
                    ? getAccessDenialReason || t("deactivated.accessRestricted")
                    : ""
                }
              >
                <FontAwesomeIcon icon={faRocket} /> {t("header.gettingStarted")}
              </a>
            ) : null}
            {isPhoneVerified && !isDeactivated && (
              <NotificationDropdown
                setHeroContent={setHeroContent}
                disabled={!hasActiveSubscription}
                disabledTitle={
                  getAccessDenialReason ||
                  "Subscribe to a plan to enable notifications."
                }
                isActive={heroContent === "Notifications"}
              />
            )}
          </>
        )}
        {/* AWS/Google Admin style selected user indicator */}
        {showViewingAs && (
          <div className="selected-user-indicator">
            <span className="viewing-as-label">{t("header.viewingAs")}</span>
            <span className="selected-user-name">
              {selectedUser.firstName} {selectedUser.lastName}
            </span>
            <button
              className="switch-back-btn"
              onClick={handleSwitchBack}
              title={t("theme.switchBack")}
            >
              {t("header.switchBack")}
            </button>
          </div>
        )}
        {isAuthenticated && !isReadOnlyView ? (
          <div className="user-dropdown-container" ref={dropdownRef}>
            <a
              href="#user"
              className="user-icon-with-text"
              onClick={(e) => {
                e.preventDefault();
                toggleUserDropdown();
              }}
            >
              <FontAwesomeIcon icon={faUser} />
              <span className="signin-text">{userName}</span>
              <FontAwesomeIcon
                icon={showUserDropdown ? faChevronUp : faChevronRight}
                className="user-menu-toggle-icon"
                aria-hidden="true"
              />
            </a>
            {showUserDropdown && (
              <div className="user-dropdown">
                {/* Theme & Language Controls */}
                <div className="theme-selector">
                  <div className="theme-options">
                    {/* Single cycling theme button */}
                    <button
                      className="theme-option active theme-option-labeled"
                      onClick={cycleTheme}
                      title={themeTitle}
                    >
                      <FontAwesomeIcon icon={themeIcon} />
                      <span className="theme-option-label">{themeLabel}</span>
                    </button>

                    {/* Language selector */}
                    <button
                      className="theme-option lang-toggle theme-option-labeled"
                      onClick={cycleLang}
                      title={t("header.lang.toggleTitle")}
                    >
                      <FontAwesomeIcon icon={faGlobe} />
                      <span className="theme-option-label">{langLabel}</span>
                    </button>
                  </div>
                </div>
                <div className="dropdown-menu-items">
                  {isPhoneVerified && canSeeAccountMenu && (
                    <a
                      href="#account-billing"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAccountClick();
                      }}
                    >
                      <FontAwesomeIcon icon={faUserShield} />{" "}
                      {t("Account & Billing")}
                    </a>
                  )}
                  {isPhoneVerified && canSeeAccountMenu && isCorporate && (
                    <a
                      href="#account-profile"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAccountProfileClick();
                      }}
                    >
                      <FontAwesomeIcon icon={faIdCard} />{" "}
                      {t("accountProfile.menuLabel")}
                    </a>
                  )}
                  {isPhoneVerified && canSeeUsersMenu && !isDeactivated && (
                    <a
                      href="#users"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUsersClick();
                      }}
                    >
                      <FontAwesomeIcon icon={faUsers} />{" "}
                      {canSeeAccountMenu
                        ? t("header.users")
                        : t("header.userProfile")}
                    </a>
                  )}

                  {isPhoneVerified && !isDeactivated && (
                    <a
                      href="#settings"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSettingsClick();
                      }}
                    >
                      <FontAwesomeIcon icon={faCog} /> {t("settings.title")}
                    </a>
                  )}
                  <a
                    href="#signout"
                    className="sign-out-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSignOutClick();
                    }}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />{" "}
                    {t("common.signOut")}
                  </a>
                </div>
              </div>
            )}
          </div>
        ) : !isReadOnlyView ? (
          <div className="user-dropdown-container signed-out">
            <a
              href="#signup"
              className="header-auth-link signup-link"
              onClick={handleClick}
              title={t("common.signUp")}
            >
              {t("common.signUp")}
            </a>
            <a
              href="#signin"
              className="header-auth-link login-link"
              onClick={handleClick}
              title={t("common.logIn")}
            >
              {t("common.logIn")}
            </a>
          </div>
        ) : null}
      </nav>
    </>
  );
}

export default HeaderAppDesktop;
